import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Lock, ArrowLeft, Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { AuthShell } from './Register';

/**
 * ResetPassword — completes the /auth/forgot-password flow. Reads
 * `?token=...` from the query string, POSTs new_password to the
 * backend, then routes the user to /login.
 */
export default function ResetPassword() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Both passwords must match.'); return; }
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      setTimeout(() => nav('/login'), 1200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not reset your password.');
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <AuthShell>
        <p className="text-center text-slate-600" data-testid="reset-missing-token">
          This reset link is invalid. <Link to="/forgot-password" className="font-bold text-[#5B8DEF]">Request a new one</Link>.
        </p>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="reset-success">
          <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-4"/>
          <h1 className="text-2xl font-extrabold text-[#2B2140]">Password updated</h1>
          <p className="text-sm text-slate-600 mt-2">Taking you to sign in…</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Link to="/login" data-testid="reset-back-btn"
        className="text-sm font-bold text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14}/> Back
      </Link>
      <h1 className="text-3xl font-extrabold text-[#2B2140] mb-1">Choose a new password</h1>
      <p className="text-sm text-slate-500 mb-6">At least 8 characters. Pick something you'll remember.</p>
      <form onSubmit={onSubmit} className="space-y-3">
        <PwField testId="reset-password" value={password} onChange={setPassword} placeholder="New password"/>
        <PwField testId="reset-confirm" value={confirm} onChange={setConfirm} placeholder="Confirm new password"/>
        {error && <p data-testid="reset-error" className="text-sm text-rose-600">{error}</p>}
        <button data-testid="reset-submit-btn" type="submit" disabled={busy}
          className="pushable w-full bg-[#FF6B1A] text-white text-lg font-bold py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin"/>}
          Save new password
        </button>
      </form>
    </AuthShell>
  );
}

function PwField({ testId, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <label className="relative flex items-center gap-3 pl-4 pr-14 rounded-2xl border-2 border-slate-200 bg-white focus-within:border-[#FF6B1A]">
      <Lock size={18} className="text-slate-400"/>
      <input data-testid={testId} type={show ? 'text' : 'password'} required minLength={8}
        placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        className="flex-1 min-w-0 bg-transparent outline-none py-3 text-[#2B2140]"/>
      <button data-testid={`${testId}-toggle`} type="button" onClick={() => setShow(v => !v)}
        aria-label={show ? 'Hide password' : 'Show password'}
        className="absolute right-1 top-1/2 -translate-y-1/2 min-w-11 min-h-11 flex items-center justify-center text-slate-600 hover:text-[#2B2140]">
        {show ? <EyeOff className="w-[1.5em] h-[1.5em]"/> : <Eye className="w-[1.5em] h-[1.5em]"/>}
      </button>
    </label>
  );
}
